import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FileIcon from './components/FileIcon';
import FilterChipStrip from './components/FilterChipStrip';
import { formatDate, formatMonthYear } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  HR document locker — shared v3 surface
//
//  Payslips, IRP5s and Performance Reviews are the same shape on both sides:
//  an employee, a period (start_date … end_date) and a single attached PDF.
//  Rather than build the screen three times, this renders all three from a
//  small config.  It is deliberately the first HR surface ported, because the
//  patterns it establishes — the HR list header, the period column, the
//  document cell, the year filter, and the self-service/HR split — are what
//  the harder HR screens (leave, expenses, timesheets) reuse.
//
//  Permissions mirror the API (HrAuthorization on the Rails side):
//    * a plain employee sees only their own records, so the Employee column
//      and the employee filter are pointless noise and are hidden;
//    * creating and editing are HR-only, so those controls are gated.
//
//  Add/Edit still route to the legacy forms.  Uploading is a base64
//  multi-step flow that has not been ported yet, and sending people to a
//  working form beats a v3 form that cannot save.
// =============================================================================

/** The common shape of a payslip / IRP5 / performance review. */
export interface LockerRecord {
  id?: string | number;
  start_date?: string | Date;
  end_date?: string | Date;
  employee?: string;
  // Payslips and IRP5s expose `user_id`; performance reviews expose
  // `employee_id` for the same thing.
  user_id?: string | number;
  employee_id?: string | number;
  document?: { name?: string; url?: string; date?: string } | string;
}

interface Props {
  /** Breadcrumb tail + page title, e.g. "Payslips". */
  title: string;
  /** Small uppercase line above the title. */
  eyebrow?: string;
  /** Singular noun used in counts and empty states, e.g. "payslip". */
  noun: string;
  records: LockerRecord[];
  /** True once the initial fetch has resolved. */
  loaded: boolean;
  /** Path of the legacy add form, e.g. "/human-resources/payslips/add". */
  legacyAddPath: string;
  /** Path builder for the legacy edit form. */
  legacyEditPath: (id: string | number) => string;
}

const documentOf = (r: LockerRecord) =>
  r.document && typeof r.document === 'object' ? r.document : undefined;

/** "Mar 2026 – Mar 2027", or a single month when the range is one point. */
const periodLabel = (r: LockerRecord): string => {
  const from = formatMonthYear(r.start_date as string);
  const to = formatMonthYear(r.end_date as string);
  if (from === '—' && to === '—') return '—';
  if (from === to) return from;
  return `${from} – ${to}`;
};

const yearOfRecord = (r: LockerRecord): string => {
  const d = r.end_date || r.start_date;
  if (!d) return '';
  const parsed = d instanceof Date ? d : new Date(d);
  return Number.isNaN(parsed.getTime()) ? '' : String(parsed.getFullYear());
};

const HrDocumentLocker = ({
  title,
  eyebrow,
  noun,
  records,
  loaded,
  legacyAddPath,
  legacyEditPath
}: Props) => {
  const navigate = useNavigate();
  const { isHrPrivileged } = useAuth();

  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('All');

  // Year chips are derived from the data rather than hardcoded, so they can
  // never go stale the way the Tombstones year list did.
  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(records.map(yearOfRecord).filter(Boolean))).sort((a, b) =>
      b.localeCompare(a)
    );
    return [{ value: 'All', label: 'All' }, ...years.map((y) => ({ value: y, label: y }))];
  }, [records]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) => {
      if (yearFilter !== 'All' && yearOfRecord(r) !== yearFilter) return false;
      if (q) {
        const doc = documentOf(r);
        const hay = `${r.employee ?? ''} ${doc?.name ?? ''} ${periodLabel(r)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, yearFilter, search]);

  // Most recent period first by default — these are chronological documents —
  // but every column is clickable.
  const SORT_ACCESSORS = useMemo(
    () => ({
      employee: (r: LockerRecord) => r.employee,
      period: (r: LockerRecord) =>
        r.end_date || r.start_date ? new Date((r.end_date || r.start_date) as string) : null,
      document: (r: LockerRecord) => documentOf(r)?.name,
      uploaded: (r: LockerRecord) => documentOf(r)?.date
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS, {
    key: 'period',
    dir: 'desc'
  });

  const withDocument = useMemo(() => records.filter((r) => documentOf(r)?.url).length, [records]);

  const subLine = () => {
    if (!loaded && records.length === 0) return 'Loading…';
    const count =
      filtered.length === records.length
        ? `${records.length} ${records.length === 1 ? noun : `${noun}s`}`
        : `${filtered.length} of ${records.length} ${noun}s`;
    const missing = records.length - withDocument;
    return [count, missing > 0 ? `${missing} without a document` : undefined]
      .filter(Boolean)
      .join(' · ');
  };

  const emptyMessage = () => {
    if (!loaded && records.length === 0) return `Loading ${noun}s…`;
    if (records.length === 0) return `No ${noun}s yet.`;
    return `No ${noun}s match these filters.`;
  };

  return (
    <>
      <Topbar crumbs={['Human Resources', title]} />
      <div className="page">
        <div className="page-header">
          <div>
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h1 className="page-title">{title}</h1>
            <div className="page-sub">{subLine()}</div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder={`Search ${noun}s…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {isHrPrivileged && (
              <button
                className="btn primary"
                type="button"
                onClick={() => navigate(legacyAddPath)}
                title="Opens the legacy upload form"
              >
                <Icon name="plus" size={14} />
                Add {noun}
              </button>
            )}
          </div>
        </div>

        {yearOptions.length > 1 && (
          <FilterChipStrip<string>
            options={yearOptions}
            value={yearFilter}
            onChange={setYearFilter}
          />
        )}

        {sorted.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">{emptyMessage()}</span>
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  {/* A plain employee only ever sees their own records, so the
                      name column would repeat their own name on every row. */}
                  {isHrPrivileged && (
                    <SortableTh label="Employee" sortKey="employee" sort={sort} onToggle={toggle} />
                  )}
                  <SortableTh label="Period" sortKey="period" sort={sort} onToggle={toggle} />
                  <SortableTh label="Document" sortKey="document" sort={sort} onToggle={toggle} />
                  <SortableTh label="Uploaded" sortKey="uploaded" sort={sort} onToggle={toggle} />
                  {isHrPrivileged && <th className="actions-cell" />}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const doc = documentOf(r);
                  return (
                    <tr key={r.id}>
                      {isHrPrivileged && <td className="strong">{r.employee || '—'}</td>}
                      <td>{periodLabel(r)}</td>
                      <td>
                        {doc?.url ? (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            className="file-cell"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                            title={`Open ${doc.name ?? 'document'}`}
                          >
                            <FileIcon filename={doc.name || 'document.pdf'} />
                            <span>{doc.name || 'Document'}</span>
                          </a>
                        ) : (
                          <span className="muted">No document</span>
                        )}
                      </td>
                      <td className="mono">{doc?.date ? doc.date : formatDate(r.start_date)}</td>
                      {isHrPrivileged && (
                        <td className="actions-cell">
                          <button
                            className="btn ghost"
                            type="button"
                            onClick={() => r.id && navigate(legacyEditPath(r.id))}
                            title="Opens the legacy edit form"
                          >
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default HrDocumentLocker;
